'use client'

import { ButtonActionType, type InteractiveButton, type Telop } from '@/types/interactiveVideo'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import ButtonOverlayPresentation from './ButtonOverlayPresentation'
import TelopDisplay from './TelopDisplay'

interface TrackingEvent {
  type: string
  buttonId: string
  buttonLabel: string
  actionType: ButtonActionType
}

export interface ButtonOverlayProps {
  buttons: InteractiveButton[]
  onButtonSelect: (buttonId: string) => void | Promise<void>
  onExternalLink?: (url: string, openInNewTab: boolean) => void
  onBack?: () => void // 戻るボタン専用のハンドラー
  trackEvent?: (event: TrackingEvent) => void
  currentNodeId?: string
  previousNodeId?: string
  telops?: Telop[]
  currentTime?: number // 現在の再生時間（秒）
  isEmbedded?: boolean // 埋め込み環境フラグ
  onRouteChange?: (targetNodeId: string) => void // Next.js環境でのルーティング用コールバック
  defaultButtonColor?: string | null // テナントのデフォルトCTAボタン色（フォールバック）
  /** 動画下部にアンケートの帯が出ている間、ボタンをその上へ上げる（帯がボタンの下に並ぶ） */
  reserveSurveyBar?: boolean
}

const ButtonOverlay: FC<ButtonOverlayProps> = ({
  buttons,
  onButtonSelect,
  onBack,
  trackEvent,
  previousNodeId,
  telops,
  currentTime = 0,
  isEmbedded = false,
  onRouteChange,
  defaultButtonColor,
  reserveSurveyBar = false,
}) => {
  // Handle button click with action branching
  const handleButtonClick = useCallback(
    async (button: InteractiveButton) => {
      // Track analytics event
      if (trackEvent) {
        trackEvent({
          type: 'button_click',
          buttonId: button.id,
          buttonLabel: button.label,
          actionType: button.type,
        })
      }

      // Handle different action types
      if (button.type === ButtonActionType.VIDEO_TRANSITION) {
        const action = button.action as { type: 'video_transition'; targetNodeId: string }

        // Update URL with new page/node (Next.js環境のみ)
        if (!isEmbedded && onRouteChange) {
          onRouteChange(action.targetNodeId)
        }

        await onButtonSelect(button.id)
      } else if (button.type === ButtonActionType.EXTERNAL_LINK) {
        // 外部リンクもonButtonSelectを経由してトラッキング完了後にページ遷移
        await onButtonSelect(button.id)
      }
    },
    [onButtonSelect, trackEvent, isEmbedded, onRouteChange],
  )

  // Handle back button click
  const handleBackClick = useCallback(() => {
    // Track analytics event for back button
    if (trackEvent) {
      trackEvent({
        type: 'button_click',
        buttonId: 'back-button',
        buttonLabel: '戻る',
        actionType: ButtonActionType.VIDEO_TRANSITION,
      })
    }

    // Call the dedicated back handler
    onBack?.()
  }, [onBack, trackEvent])

  // State for showing/hiding all buttons
  const [showAllButtons, setShowAllButtons] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Monitor scroll position
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 15

      if (isAtBottom) {
        setShowAllButtons(false)
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle expand button click
  const handleExpandClick = useCallback(() => {
    setShowAllButtons(true)
    // Scroll to top
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [])

  return (
    <ButtonOverlayPresentation
      buttons={buttons}
      onClick={handleButtonClick}
      onBackClick={handleBackClick}
      showBackButton={!!previousNodeId}
      TelopDisplay={TelopDisplay}
      telops={telops}
      currentTime={currentTime}
      showAllButtons={showAllButtons}
      scrollContainerRef={scrollContainerRef}
      onExpandClick={handleExpandClick}
      defaultButtonColor={defaultButtonColor}
      reserveSurveyBar={reserveSurveyBar}
    />
  )
}

export default ButtonOverlay
